import type { ContentHierarchyResponse, ContentHierarchyNode } from '@/api/content'
import type { CsvCourse } from '@/data/curriculumCsvData'

// ============================================================================
// Types
// ============================================================================

export type MatchStatus = 'matched' | 'order_mismatch' | 'csv_only' | 'system_only'

export interface ComparisonItem {
  status: MatchStatus
  title: string
  qid?: string
  systemLessonId?: number
  csvIndex?: number
  systemIndex?: number
}

export interface CourseComparison {
  courseName: string
  csvCourseName?: string
  systemCourseName?: string
  csvCount: number
  systemCount: number
  items: ComparisonItem[]
}

export interface ComparisonSummary {
  matched: number
  orderMismatch: number
  csvOnly: number
  systemOnly: number
}

export interface ComparisonResult {
  summary: ComparisonSummary
  courses: CourseComparison[]
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * hierarchy에서 모든 lesson 노드를 재귀적으로 추출
 */
function extractLessons(node: ContentHierarchyNode): ContentHierarchyNode[] {
  const lessons: ContentHierarchyNode[] = []
  if (node.type === 'lesson') {
    lessons.push(node)
  }
  for (const child of node.children) {
    lessons.push(...extractLessons(child))
  }
  return lessons
}

/**
 * hierarchy에서 course 노드를 추출
 */
function extractCourses(hierarchy: ContentHierarchyResponse): ContentHierarchyNode[] {
  return hierarchy.children.filter(c => c.type === 'course')
}

/**
 * course 이름 매칭: CSV 이름이 시스템 title/bareTitle에 포함되어 있으면 매칭
 * 공백/특수문자 제거 후 비교하여 "메타코어" ↔ "메타 코어" 등의 차이를 허용
 */
function matchCourseName(systemCourse: ContentHierarchyNode, csvCourseName: string): boolean {
  const normalize = (s: string) => s.replace(/[\s_\-[\]()]/g, '').toLowerCase()
  const csvNorm = normalize(csvCourseName)
  if (normalize(systemCourse.title).includes(csvNorm)) return true
  if (systemCourse.bareTitle && normalize(systemCourse.bareTitle).includes(csvNorm)) return true
  return false
}

// ============================================================================
// Main Comparison Function
// ============================================================================

export function compareCsvWithHierarchies(
  csvData: CsvCourse[],
  hierarchies: ContentHierarchyResponse[]
): ComparisonResult {
  const systemCourses = hierarchies.flatMap(h => extractCourses(h))
  const courses: CourseComparison[] = []
  let totalMatched = 0
  let totalOrderMismatch = 0
  let totalCsvOnly = 0
  let totalSystemOnly = 0

  // 시스템 course 중 이미 매칭된 것 추적
  const matchedSystemCourseIds = new Set<number>()

  // CSV의 각 Course에 대해 시스템 course 매칭
  for (const csvCourse of csvData) {
    const systemCourse = systemCourses.find(
      sc => !matchedSystemCourseIds.has(sc.id) && matchCourseName(sc, csvCourse.name)
    )

    if (systemCourse) {
      matchedSystemCourseIds.add(systemCourse.id)
    }

    // 시스템 course 내 모든 lesson 추출
    const systemLessons = systemCourse
      ? systemCourse.children.flatMap(child => extractLessons(child))
      : []

    // qid 기반 매칭
    const systemLessonByQid = new Map<string, ContentHierarchyNode>()
    for (const lesson of systemLessons) {
      if (lesson.qid) {
        systemLessonByQid.set(lesson.qid, lesson)
      }
    }

    const matchedSystemLessonIds = new Set<number>()
    const items: ComparisonItem[] = []

    // CSV 레슨을 순회하며 매칭
    for (const csvLesson of csvCourse.lessons) {
      const systemLesson = systemLessonByQid.get(csvLesson.qid)

      if (systemLesson) {
        matchedSystemLessonIds.add(systemLesson.id)
        items.push({
          status: 'matched',
          title: csvLesson.title,
          qid: csvLesson.qid,
          systemLessonId: systemLesson.id,
        })
      } else {
        items.push({
          status: 'csv_only',
          title: csvLesson.title,
          qid: csvLesson.qid,
        })
        totalCsvOnly++
      }
    }

    // 순서 비교: 매칭된 레슨의 CSV 순서 vs 시스템 순서
    const csvQidSet = new Set(csvCourse.lessons.map(l => l.qid))

    const csvOrderedMatchedQids = csvCourse.lessons
      .map(l => l.qid)
      .filter(qid => systemLessonByQid.has(qid))

    const systemOrderedMatchedQids = systemLessons
      .filter(l => l.qid != null && csvQidSet.has(l.qid!))
      .map(l => l.qid!)

    const csvPosMap = new Map<string, number>()
    csvOrderedMatchedQids.forEach((qid, i) => csvPosMap.set(qid, i))

    const sysPosMap = new Map<string, number>()
    systemOrderedMatchedQids.forEach((qid, i) => sysPosMap.set(qid, i))

    for (const item of items) {
      if (item.status === 'matched' && item.qid) {
        const csvPos = csvPosMap.get(item.qid)!
        const sysPos = sysPosMap.get(item.qid)!
        item.csvIndex = csvPos
        item.systemIndex = sysPos
        if (csvPos !== sysPos) {
          item.status = 'order_mismatch'
          totalOrderMismatch++
        } else {
          totalMatched++
        }
      }
    }

    // 시스템에만 있는 레슨 추가
    for (const lesson of systemLessons) {
      if (!matchedSystemLessonIds.has(lesson.id)) {
        items.push({
          status: 'system_only',
          title: lesson.bareTitle || lesson.title,
          qid: lesson.qid ?? undefined,
          systemLessonId: lesson.id,
        })
        totalSystemOnly++
      }
    }

    courses.push({
      courseName: csvCourse.name,
      csvCourseName: csvCourse.name,
      systemCourseName: systemCourse
        ? (systemCourse.bareTitle || systemCourse.title)
        : undefined,
      csvCount: csvCourse.lessons.length,
      systemCount: systemLessons.length,
      items,
    })
  }

  // 시스템에만 있는 course (CSV에 없는 course)
  for (const systemCourse of systemCourses) {
    if (!matchedSystemCourseIds.has(systemCourse.id)) {
      const systemLessons = systemCourse.children.flatMap(child => extractLessons(child))
      const items: ComparisonItem[] = systemLessons.map(lesson => ({
        status: 'system_only' as MatchStatus,
        title: lesson.bareTitle || lesson.title,
        qid: lesson.qid ?? undefined,
        systemLessonId: lesson.id,
      }))

      totalSystemOnly += systemLessons.length

      courses.push({
        courseName: systemCourse.bareTitle || systemCourse.title,
        systemCourseName: systemCourse.bareTitle || systemCourse.title,
        csvCount: 0,
        systemCount: systemLessons.length,
        items,
      })
    }
  }

  return {
    summary: {
      matched: totalMatched,
      orderMismatch: totalOrderMismatch,
      csvOnly: totalCsvOnly,
      systemOnly: totalSystemOnly,
    },
    courses,
  }
}
